// バーコードスキャン入力（入荷・棚卸し・在庫調整で共用）— Stocky parity v2 Step 4。
//
// 🔑 **前提＝USB/BT スキャナはキーボードとして「コード + Enter」を打つ**。だから
// 特別な API もネイティブアプリも要らない。要るのは「打ち込み先の入力欄が、常に
// 空でフォーカスされていること」だけ。
//
// 🔴 **この画面の唯一の不変条件＝1回スキャンするごとに欄を空にしてフォーカスを戻す。**
// 倉庫では両手が塞がっていてマウスを触れない。フォーカスが外れると次のスキャンは
// どこにも入らず「無言で消える」＝作業者は打ったつもりで数え漏らす。成功でも失敗でも
// 必ず戻す（失敗時こそ戻さないと、エラーを読んでいる間に打った次の1件が消える）。
//
// カメラ読み取り（BarcodeDetector）は iOS のサポートが弱いのでやらない（計画書の決定）。
import { Banner, BlockStack, Button, InlineStack, Text, TextField } from "@shopify/polaris";
import { useMutation } from "@tanstack/react-query";
import { useId, useRef, useState } from "react";

import {
  lookupVariantByCode,
  variantMatchLabel,
  type VariantMatch,
} from "../api/variants";

import { t } from "../lib/i18n";

interface ScanInputProps {
  /** 1件に確定したときに呼ばれる。呼び出し側が「数量を+1する」等の意味を与える。 */
  onResolved: (variant: VariantMatch) => void;
  /**
   * サーバーの lookup が失敗したときに、手元のデータで解決を試みる。
   * 🔴 入荷画面のためにある＝発注後に Shopify 側で商品が消える（終売判定になる）と
   * カタログでは引けなくなるが、**発注書の明細には載っている**＝荷物は受け取れる
   * べき。null を返したらサーバーの理由をそのまま出す。
   */
  resolveFallback?: (code: string) => VariantMatch | null;
  /** 確定した商品がこの画面の対象外だったときに返す日本語の理由。null なら受理。
   *  （例: 棚卸しの対象に入っていない／この発注書に含まれていない） */
  validate?: (variant: VariantMatch) => string | null;
  label?: string;
  helpText?: string;
  disabled?: boolean;
}

export function ScanInput({
  onResolved,
  resolveFallback,
  validate,
  label = t("バーコードをスキャン"),
  helpText = t("バーコードリーダーで読み取るか、バーコード／SKU を入力して Enter を押してください。"),
  disabled = false,
}: ScanInputProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLDivElement>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<VariantMatch[]>([]);
  const [lastAccepted, setLastAccepted] = useState<string | null>(null);

  // 欄を空にしてフォーカスを戻す＝次のスキャンの受け皿を必ず用意する。
  const resetForNextScan = () => {
    setCode("");
    inputRef.current?.querySelector("input")?.focus();
  };

  const accept = (variant: VariantMatch) => {
    const rejection = validate?.(variant) ?? null;
    if (rejection) {
      setError(rejection);
      setLastAccepted(null);
      setCandidates([]);
      resetForNextScan();
      return;
    }
    onResolved(variant);
    setError(null);
    setCandidates([]);
    setLastAccepted(variantMatchLabel(variant));
    resetForNextScan();
  };

  const mutation = useMutation({
    mutationFn: lookupVariantByCode,
    onSuccess: (result) => {
      if (result.variants.length === 1) {
        accept(result.variants[0]);
        return;
      }
      // 🔴 同じバーコードが複数商品に付いている＝勝手に1件へ絞ると別商品の在庫を
      // 動かす。選ばせる（backend が候補を全部返す設計と対）。
      setError(null);
      setLastAccepted(null);
      setCandidates(result.variants);
      resetForNextScan();
    },
    onError: (err: unknown, code: string) => {
      // カタログで引けなくても、呼び出し側が手元のデータ（発注書の明細）で
      // 解決できるなら受理する。validate は通常経路と同じに通る。
      const fallback = resolveFallback?.(code.trim()) ?? null;
      if (fallback !== null) {
        accept(fallback);
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
      setLastAccepted(null);
      setCandidates([]);
      // 失敗時も欄は空にする＝読み取り損ねたコードの後ろに次のスキャンが
      // 連結されて「別の意味不明なコード」になるのを防ぐ。
      resetForNextScan();
    },
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = code.trim();
    // 空打ち（フォーカスした状態で Enter だけ）ではサーバーを叩かない。
    if (trimmed === "" || mutation.isPending || disabled) return;
    mutation.mutate(trimmed);
  };

  return (
    <form onSubmit={handleSubmit}>
      <BlockStack gap="200">
        <div ref={inputRef}>
          <TextField
            id={inputId}
            label={label}
            value={code}
            onChange={setCode}
            autoComplete="off"
            disabled={disabled}
            helpText={helpText}
            connectedRight={
              <Button submit loading={mutation.isPending} disabled={disabled}>
                {t("読み取り")}
              </Button>
            }
          />
        </div>
        {lastAccepted && (
          <Text as="p" tone="success">
            {t("読み取り: {code}", { code: lastAccepted })}
          </Text>
        )}
        {error && (
          <Banner tone="warning" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        {candidates.length > 0 && (
          <Banner tone="warning">
            <BlockStack gap="200">
              <Text as="p">
                {t("同じコードの商品が {n}", { n: candidates.length })}{" "}
                {t("件あります。どれかを選んでください。")}
              </Text>
              <InlineStack gap="200" wrap>
                {candidates.map((variant) => (
                  <Button
                    key={variant.shopify_variant_id}
                    onClick={() => accept(variant)}
                  >
                    {variantMatchLabel(variant)}
                  </Button>
                ))}
              </InlineStack>
            </BlockStack>
          </Banner>
        )}
      </BlockStack>
    </form>
  );
}
